import { Button } from "@chakra-ui/react/button";
import { Circle } from "@chakra-ui/react/circle";
import { Flex } from "@chakra-ui/react/flex";
import { Float } from "@chakra-ui/react/float";
import { Icon } from "@chakra-ui/react/icon";

import {
  HoverCardArrow,
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
} from "components/ui/hover-card";
import { useUnreadCounts } from "hooks/queries/use-current-agent";
import React from "react";
import {
  LuBot,
  LuCalendar,
  LuChartArea,
  LuCircuitBoard,
  LuCreditCard,
  LuMail,
  LuMegaphone,
  LuSettings,
  LuStar,
  LuUser,
  LuWrench,
} from "react-icons/lu";
import { NavLink } from "react-router-dom";
import { useAppSelector } from "redux/hooks";

interface DesktopNavButtonProps {
  icon: React.ReactNode;
  name: string;
  link: string;
  isDisabled?: boolean;
  onClick?: () => void;
}

const DesktopNavButton = ({
  icon,
  name,
  link,
  isDisabled,
  onClick,
}: DesktopNavButtonProps) => {
  const { colorScheme } = useAppSelector((state) => state.theme);

  return (
    <HoverCardRoot positioning={{ placement: "right" }}>
      <HoverCardTrigger asChild>
        <Button
          aria-label={`${name}-navigation-button`}
          disabled={isDisabled}
          colorPalette="blackAlpha"
          color="white"
          variant="ghost"
          className="dark"
          borderRadius="lg"
          width="3rem"
          height="3rem"
          m={2}
          asChild
          // FREO: Active-page indicator on the always-dark sidebar; the
          // parent surface is gray.900 light / gray.800 dark, so the active
          // tint must darken in light mode and deepen in dark mode. No
          // semantic token captures the "darker-than-parent on a dark chrome"
          // role.
          _currentPage={{
            bgColor: {
              _light: "gray.700",
              _dark: "gray.900",
            },
          }}
        >
          <NavLink
            to={link}
            onClick={onClick}
            style={{
              padding: "1rem",
            }}
          >
            {icon}
          </NavLink>
        </Button>
      </HoverCardTrigger>
      <HoverCardContent>
        <HoverCardArrow />
        {name}
      </HoverCardContent>
    </HoverCardRoot>
  );
};

export const InboxLink = () => {
  const { data: unreadCounts = [] } = useUnreadCounts();
  const merchantId = useAppSelector((state) => state.merchant.merchantId);
  const unreadCount = merchantId
    ? unreadCounts.find((uc) => uc.merchant_id === merchantId)
    : undefined;

  if (!merchantId) {
    return null;
  }

  return (
    <Flex position="relative" alignItems="center">
      <DesktopNavButton
        icon={
          <Flex position="relative" alignItems="center" justifyContent="center">
            <Icon as={LuMail} boxSize={7} color="fg.default" />
            {unreadCount?.personal || unreadCount?.shared ? (
              <>
                <Float offsetX={-2} offsetY={-2}>
                  <Circle height="1rem" width="1rem" bgColor="red.solid" />
                </Float>
              </>
            ) : null}
          </Flex>
        }
        name="Inbox"
        link={`/${merchantId}/inbox`}
      />
    </Flex>
  );
};

export const AutomationsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={
        <Icon
          as={LuCircuitBoard}
          width="2rem"
          height="2rem"
          color="fg.default"
        />
      }
      name="Automations"
      link={`/${merchantId}/automations`}
    />
  );
};

export const ContactLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={<Icon as={LuUser} width="2rem" height="2rem" color="fg.default" />}
      name="Contacts"
      link={`/${merchantId}/contacts`}
    />
  );
};

export const AiChatsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={<Icon as={LuBot} width="2rem" height="2rem" color="fg.default" />}
      name="AI Chats"
      link={`/${merchantId}/ai-chats`}
    />
  );
};

export const CampaignsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={
        <Flex position="relative">
          <Icon
            as={LuMegaphone}
            width="2rem"
            height="2rem"
            color="fg.default"
          />
        </Flex>
      }
      name="Campaigns"
      link={`/${merchantId}/campaigns`}
    />
  );
};

export const CalendarLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={
        <Icon as={LuCalendar} width="2rem" height="2rem" color="fg.default" />
      }
      name="Calendar"
      link={`/${merchantId}/calendar`}
    />
  );
};

export const PaymentsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={
        <Icon as={LuCreditCard} width="2rem" height="2rem" color="fg.default" />
      }
      name="Payments"
      link={`/${merchantId}/payments`}
    />
  );
};

export const ReviewsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={<Icon as={LuStar} width="2rem" height="2rem" color="fg.default" />}
      name="Reviews"
      link={`/${merchantId}/reviews`}
    />
  );
};

export const AnalyticsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={
        <Icon as={LuChartArea} width="2rem" height="2rem" color="fg.default" />
      }
      name="Analytics"
      link={`/${merchantId}/analytics`}
    />
  );
};

export const AftersalesLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={
        <Icon as={LuWrench} width="2rem" height="2rem" color="fg.default" />
      }
      name="Aftersales"
      link={`/${merchantId}/aftersales`}
    />
  );
};

export const SettingsLink = () => {
  const merchantId = useAppSelector((state) => state.merchant.merchantId);

  if (!merchantId) {
    return null;
  }

  return (
    <DesktopNavButton
      icon={
        <Icon as={LuSettings} width="2rem" height="2rem" color="fg.default" />
      }
      name="Settings"
      link={`/${merchantId}/settings`}
    />
  );
};

export default DesktopNavButton;
